/**
 * GoalCard — premium minimalist card.
 * Shows: header (icon · name · category · status · year), large progress bar,
 * roadmap (numeric) or inline steps grid (steps), add-entry CTA.
 */

import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Lock, Globe, Plus, AlertTriangle } from 'lucide-react'
import { useGoalsStore } from '@turnifly/shared/stores'
import { useDeleteGoal, useGoalEntries, useAnnounceGoal } from '@turnifly/shared/hooks'
import { useCategories } from '@turnifly/shared/hooks'
import { calculateProgress, getProgressColor, getDeadlineInfo } from '@turnifly/shared/utils'
import { GoalRoadmap } from './goal-roadmap'
import { GoalCardMenu } from './goal-card-menu'
import { GoalDeleteSheet } from './goal-delete-sheet'
import { GoalStepsInline } from './goal-steps-inline'
import type { Goal } from '@turnifly/shared/types'

interface GoalCardProps {
  goal: Goal
  index: number
}

const STATUS_BADGE: Record<string, string> = {
  planned:     'text-text-hint bg-white/[0.07]',
  in_progress: 'text-primary bg-primary/[0.12]',
  achieved:    'text-success bg-success/[0.12]',
  incomplete:  'text-warning bg-warning/[0.12]',
}

function progressBarColor(percent: number): string {
  if (percent >= 80) return 'bg-success'
  if (percent >= 60) return 'bg-streak'
  if (percent >= 40) return 'bg-primary'
  return 'bg-danger'
}

export const GoalCard: React.FC<GoalCardProps> = React.memo(({ goal, index }) => {
  const { t, i18n } = useTranslation()
  const { data: categories } = useCategories()
  const setEditingGoal  = useGoalsStore((s) => s.setEditingGoal)
  const setViewingGoalId = useGoalsStore((s) => s.setViewingGoalId)
  const openEntrySheet  = useGoalsStore((s) => s.openEntrySheet)
  const deleteMutation  = useDeleteGoal()
  const announceMutation = useAnnounceGoal()

  const isSteps   = goal.goal_type === 'steps'
  const isAchieved = goal.status === 'achieved'
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  // Entries needed only for steps (inline checklist state)
  const { data: entries } = useGoalEntries(isSteps ? goal.id : null)

  const progress     = calculateProgress(goal)
  const progressColor = getProgressColor(progress)
  const barColor     = progressBarColor(progress)
  const deadlineInfo = getDeadlineInfo(goal)

  // Resolved category label
  const categoryLabel = (() => {
    if (!goal.category) return null
    const lang = i18n.language === 'uz' ? 'name_uz' : 'name_en'
    const cats = (categories ?? []) as Array<Record<string, string>>
    const cat  = cats.find((c) => c.id === goal.category)
    return cat ? (cat[lang] ?? cat['name_en'] ?? goal.category) : goal.category
  })()

  // "12/50 kitob"  or  "3/7 qadam"
  const progressLabel = `${goal.current_value}/${goal.target_value} ${goal.unit}`

  const handleEdit    = useCallback((): void => { setEditingGoal(goal) }, [goal, setEditingGoal])
  const handleDelete  = useCallback((): void => { setIsDeleteOpen(true) }, [])
  const handleConfirmDelete = useCallback((): void => {
    deleteMutation.mutate(goal.id, { onSettled: () => setIsDeleteOpen(false) })
  }, [goal.id, deleteMutation])
  const handleAnnounce  = useCallback((): void => { announceMutation.mutate(goal) }, [goal, announceMutation])
  const handleAddEntry  = useCallback((): void => { openEntrySheet(goal.id) }, [goal.id, openEntrySheet])

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setViewingGoalId(goal.id)}
        className={`rounded-2xl p-4 mb-3 cursor-pointer transition-all duration-200 active:scale-[0.995] ${
          isAchieved
            ? 'bg-success/[0.04] border border-success/[0.18] shadow-[0_0_32px_rgba(34,197,94,0.06)]'
            : 'bg-[var(--color-surface)] border border-white/[0.07] hover:border-white/[0.13]'
        }`}
      >
        {/* ── Header ── */}
        <div className="flex items-start gap-3 mb-3">
          {/* Icon */}
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 ${
            isAchieved ? 'bg-success/[0.1]' : 'bg-white/[0.06]'
          }`}>
            {goal.icon}
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-1.5 mb-1">
              <h3 className="text-[14px] font-display font-semibold text-text leading-snug truncate">
                {goal.name}
              </h3>
              {goal.is_private
                ? <Lock size={11} className="text-text-hint/60 shrink-0" />
                : <Globe size={11} className="text-text-hint/30 shrink-0" />
              }
            </div>

            {/* Category · Status · Year */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {categoryLabel && (
                <span className="text-[11px] text-text-hint/70 leading-none">{categoryLabel}</span>
              )}
              {categoryLabel && (
                <span className="text-[10px] text-text-hint/25">·</span>
              )}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none ${
                STATUS_BADGE[goal.status] ?? STATUS_BADGE.planned
              }`}>
                {t(`goals.status.${goal.status}`)}
              </span>
              <span className="text-[10px] text-text-hint/25">·</span>
              <span className="text-[11px] text-text-hint/40 tabular-nums leading-none">{goal.year}</span>
            </div>
          </div>

          {/* Menu */}
          <GoalCardMenu
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAnnounce={handleAnnounce}
            isAnnounced={goal.is_announced}
          />
        </div>

        {/* ── Description (optional, max 70 chars) ── */}
        {goal.description && (
          <p className="text-[11px] text-text-hint/60 leading-relaxed mb-3 -mt-1">
            {goal.description.length > 70
              ? goal.description.slice(0, 70) + '…'
              : goal.description}
          </p>
        )}

        {/* ── Progress ── */}
        <div className="mb-3">
          <div className="flex items-baseline justify-between mb-2">
            <span className={`text-[13px] font-semibold tabular-nums ${progressColor}`}>
              {progressLabel}
            </span>
            <span className="text-[11px] font-medium text-text-hint tabular-nums">
              {progress}%
            </span>
          </div>

          {/* Bar */}
          <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${barColor}`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: index * 0.06 + 0.15 }}
            />
          </div>

          {/* Deadline alerts */}
          {deadlineInfo.status === 'warning' && (
            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-warning font-medium">
              <AlertTriangle size={10} />
              {t('goals.deadlineWarning', { days: deadlineInfo.daysLeft })}
            </div>
          )}
          {deadlineInfo.status === 'overdue' && (
            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-danger font-medium">
              <AlertTriangle size={10} />
              {t('goals.deadlineOverdue')}
            </div>
          )}
        </div>

        {/* ── Steps inline OR roadmap ── */}
        {isSteps ? (
          <div onClick={(e) => e.stopPropagation()}>
            <GoalStepsInline
              steps={goal.steps ?? []}
              entries={entries ?? []}
              goalId={goal.id}
              isOwner
            />
          </div>
        ) : (
          <GoalRoadmap goal={goal} />
        )}

        {/* ── Add Entry CTA (numeric + not achieved) ── */}
        {!isSteps && !isAchieved && (
          <div
            className="mt-3 pt-3 border-t border-white/[0.05]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleAddEntry}
              className="group flex items-center gap-2 text-primary/70 hover:text-primary transition-colors tap-highlight-transparent"
            >
              <div className="w-6 h-6 rounded-lg bg-primary/[0.1] group-hover:bg-primary/[0.16] flex items-center justify-center transition-colors">
                <Plus size={12} />
              </div>
              <span className="text-[12px] font-medium">{t('goals.addEntry')}</span>
            </button>
          </div>
        )}
      </motion.div>

      {/* Delete confirmation */}
      <GoalDeleteSheet
        isOpen={isDeleteOpen}
        goalName={goal.name}
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteOpen(false)}
      />
    </>
  )
})

GoalCard.displayName = 'GoalCard'
